import { ClickToCopyRemotePromoId } from "@/components/click_to_copy_remote_promo_id.tsx"
import { useState, useMemo } from "preact/hooks"
import useSWR from "swr"
import { parseISO, format, startOfMonth, endOfMonth } from "date-fns"
import { api_fetch } from "../../../api/client"
import { exactCurrencyFormatter } from "../../../utils"
import { Promotion } from "../promo_creation/types"
import { CalendarFilters } from "./components/calendar_filters"
import { PromotionDownloads } from "./components/promotion_downloads"
import { PromotionFilterSelection, promotion_filter_options, promotion_matches_filters } from "./promotion_filters"
import { Loader2, ArrowUp, ArrowDown, ChevronsUpDown } from "lucide-react"
import { LastRunReference, format_ratio, last_run_warning } from "../promo_creation/last_run_check"
import { last_run_title } from "../promo_creation/components/last_run_notice"

// ── Types ─────────────────────────────────────────────────────────────────────

export interface AccrualRow {
  id: string
  remote_promo_id: string | null
  name: string
  retailer: string | null
  customer: string | null
  promotion_type: string | null
  start_date: string | null
  end_date: string | null
  calendar_id: string
  calendar_name: string
  planned_amount: number | null
  latest_estimate_amount: number | null
  planned_volume_cases: number | null
  planned_volume_units: number | null
  actual_amount: number
  match_count: number
  invoice_months: number[]
  check_months: number[]
  /** What the same event spent the last times it ran; null for a first-time event. */
  last_run?: LastRunReference | null
}

// ── Sorting & month-filter types ────────────────────────────────────────────────

export type SortKey =
  | "promo"
  | "retailer"
  | "customer"
  | "type"
  | "dates"
  | "planned"
  | "latest_estimate"
  | "actual"
  | "variance"
  | "variance_pct"
  | "matches"
  | "status"

export type SortDir = "asc" | "desc"

/** Which date dimension the month chips filter on. */
export type MonthBasis = "promotion" | "invoice" | "check"

const MONTH_BASIS_OPTIONS: { value: MonthBasis; label: string; hint: string }[] = [
  { value: "promotion", label: "Promotion execution date", hint: "Promotion start/end dates" },
  { value: "invoice", label: "Deduction invoice date", hint: "Invoice date of matched deductions" },
  { value: "check", label: "Check date", hint: "Check/payment date of matched deductions" },
]

interface AccrualSummaryViewProps {
  selectedYear: number
  onEditPromotion: (promotion: Promotion) => void
  /** The same chip filters and search as the Calendar view, owned by the parent. */
  filters: PromotionFilterSelection
  onFiltersChange: (filters: PromotionFilterSelection) => void
  searchTerm: string
  onSearchChange: (term: string) => void
  className?: string
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]

// ── Helpers ───────────────────────────────────────────────────────────────────

function promotion_overlaps_month(row: AccrualRow, monthIndex: number, year: number): boolean {
  const monthStart = startOfMonth(new Date(year, monthIndex, 1))
  const monthEnd = endOfMonth(new Date(year, monthIndex, 1))
  const promoStart = row.start_date ? parseISO(row.start_date) : null
  const promoEnd = row.end_date ? parseISO(row.end_date) : null
  if (!promoStart && !promoEnd) return true
  const effectiveStart = promoStart ?? monthStart
  const effectiveEnd = promoEnd ?? monthEnd
  return effectiveStart <= monthEnd && effectiveEnd >= monthStart
}

/**
 * Whether a row matches the selected months for the given date basis.
 * `selectedMonths` are 0-indexed (Jan = 0); the backend month arrays are 1-indexed.
 */
export function row_matches_months(
  row: AccrualRow,
  selectedMonths: Set<number>,
  basis: MonthBasis,
  year: number,
): boolean {
  if (selectedMonths.size === 0) return true
  if (basis === "promotion") {
    return Array.from(selectedMonths).some((m) => promotion_overlaps_month(row, m, year))
  }
  const months = basis === "invoice" ? row.invoice_months : row.check_months
  return Array.from(selectedMonths).some((m) => months.includes(m + 1))
}

/** The current best spend estimate: the in-year re-forecast, falling back to the plan. */
export function latest_estimate_of(row: AccrualRow): number | null {
  return row.latest_estimate_amount ?? row.planned_amount
}

/** Loads the accrual rows for a year. Shared by the Accrual page and the Calendar's spend strip. */
export function use_accrual_rows(year: number) {
  const { data, error, isLoading } = useSWR(["promotions-accrual-summary", year], async () => {
    const res = await api_fetch<AccrualRow[]>(`/promotions/accrual-summary`, { params: { year } })
    if (!res.ok) throw new Error("Failed to load accrual data")
    return res.value.data
  })
  return { rows: data ?? [], error: error ? "Failed to load accrual data" : null, loading: isLoading }
}

export interface AccrualTotals {
  promotions: number
  planned: number
  latest_estimate: number
  actual: number
  /** Actual minus planned: positive is over plan. */
  variance: number
  with_plan: number
  with_own_estimate: number
}

function to_cents(amount: number): number {
  return Math.round(amount * 100)
}

/** Planned, latest estimate and actual spend summed over rows, added in cents. */
export function accrual_totals(rows: AccrualRow[]): AccrualTotals {
  let planned = 0
  let latest_estimate = 0
  let actual = 0
  for (const row of rows) {
    planned += to_cents(row.planned_amount ?? 0)
    latest_estimate += to_cents(latest_estimate_of(row) ?? 0)
    actual += to_cents(row.actual_amount)
  }
  return {
    promotions: rows.length,
    planned: planned / 100,
    latest_estimate: latest_estimate / 100,
    actual: actual / 100,
    variance: (actual - planned) / 100,
    with_plan: rows.filter((r: AccrualRow) => r.planned_amount !== null).length,
    with_own_estimate: rows.filter((r: AccrualRow) => r.latest_estimate_amount !== null).length,
  }
}

function variance_of(row: AccrualRow): number | null {
  return row.planned_amount !== null ? row.actual_amount - row.planned_amount : null
}

function variance_pct_of(row: AccrualRow): number | null {
  return row.planned_amount ? (row.actual_amount - row.planned_amount) / row.planned_amount * 100 : null
}

/** Comparable value for a sort key. Strings sort case-insensitively; nulls sort last. */
export function sort_value(row: AccrualRow, key: SortKey): string | number | null {
  switch (key) {
    case "promo":
      return row.remote_promo_id?.toLowerCase() ?? null
    case "retailer":
      return row.retailer?.toLowerCase() ?? null
    case "customer":
      return row.customer?.toLowerCase() ?? null
    case "type":
      return row.promotion_type?.toLowerCase() ?? null
    case "dates":
      return row.start_date ?? null
    case "planned":
      return row.planned_amount
    case "latest_estimate":
      return latest_estimate_of(row)
    case "actual":
      return row.actual_amount
    case "variance":
      return variance_of(row)
    case "variance_pct":
      return variance_pct_of(row)
    case "matches":
      return row.match_count
    case "status":
      return promotion_status(row)
  }
}

export function compare_rows(a: AccrualRow, b: AccrualRow, key: SortKey, dir: SortDir): number {
  const av = sort_value(a, key)
  const bv = sort_value(b, key)
  // Nulls always sort last, regardless of direction.
  if (av === null && bv === null) return 0
  if (av === null) return 1
  if (bv === null) return -1
  let cmp: number
  if (typeof av === "number" && typeof bv === "number") {
    cmp = av - bv
  } else {
    cmp = String(av).localeCompare(String(bv))
  }
  return dir === "asc" ? cmp : -cmp
}

function promotion_status(row: AccrualRow): "open" | "closed" | "no_date" {
  if (!row.end_date) return "no_date"
  return parseISO(row.end_date) >= new Date() ? "open" : "closed"
}

function format_date_range(start: string | null, end: string | null): string {
  const s = start ? format(parseISO(start), "MMM d, yy") : null
  const e = end ? format(parseISO(end), "MMM d, yy") : null
  if (s && e) return `${s} – ${e}`
  if (s) return `from ${s}`
  if (e) return `until ${e}`
  return "—"
}

// ── Sub-components ────────────────────────────────────────────────────────────

interface StatusBadgeProps {
  status: "open" | "closed" | "no_date"
}

function StatusBadge({ status }: StatusBadgeProps) {
  const config = {
    open: { label: "Open", cls: "bg-green-100 text-green-800" },
    closed: { label: "Closed", cls: "bg-gray-100 text-gray-600" },
    no_date: { label: "No Date", cls: "bg-yellow-100 text-yellow-700" },
  }[status]
  return (
    <span class={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${config.cls}`}>
      {config.label}
    </span>
  )
}

interface ProgressBarProps {
  actual: number
  planned: number
}

function ProgressBar({ actual, planned }: ProgressBarProps) {
  const pct = Math.min((actual / planned) * 100, 120)
  const over = actual > planned
  return (
    <div class="mt-1 h-1.5 w-full rounded-full bg-gray-100 overflow-hidden">
      <div
        class={`h-full rounded-full transition-all ${over ? "bg-red-400" : "bg-emerald-400"}`}
        style={{ width: `${Math.min(pct, 100)}%` }}
      />
    </div>
  )
}

interface MonthChipsProps {
  selectedMonths: Set<number>
  onToggle: (idx: number) => void
}

function MonthChips({ selectedMonths, onToggle }: MonthChipsProps) {
  return (
    <div class="flex flex-wrap gap-1">
      {MONTHS.map((m, i) => (
        <button
          key={m}
          onClick={() => onToggle(i)}
          class={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
            selectedMonths.has(i)
              ? "bg-blue-600 text-white border-blue-600"
              : "bg-white text-gray-600 border-gray-300 hover:border-blue-400"
          }`}
        >
          {m}
        </button>
      ))}
      {selectedMonths.size > 0 && (
        <button
          onClick={() => MONTHS.forEach((_, i) => selectedMonths.has(i) && onToggle(i))}
          class="px-2.5 py-1 rounded-full text-xs font-medium border border-gray-200 text-gray-400 hover:text-gray-600"
        >
          Clear
        </button>
      )}
    </div>
  )
}

interface MonthBasisSelectorProps {
  value: MonthBasis
  onChange: (v: MonthBasis) => void
}

function MonthBasisSelector({ value, onChange }: MonthBasisSelectorProps) {
  return (
    <div class="inline-flex rounded-md border border-gray-300 overflow-hidden">
      {MONTH_BASIS_OPTIONS.map((opt) => (
        <button
          key={opt.value}
          type="button"
          title={opt.hint}
          onClick={() => onChange(opt.value)}
          class={`px-3 py-1 text-xs font-medium border-r border-gray-300 last:border-r-0 transition-colors ${
            value === opt.value
              ? "bg-blue-600 text-white"
              : "bg-white text-gray-600 hover:bg-gray-50"
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  )
}

interface SortHeaderProps {
  label: string
  sortKey: SortKey
  activeKey: SortKey
  dir: SortDir
  onSort: (key: SortKey) => void
  align?: "left" | "right" | "center"
}

function SortHeader({ label, sortKey, activeKey, dir, onSort, align = "left" }: SortHeaderProps) {
  const active = activeKey === sortKey
  const alignCls = align === "right" ? "justify-end" : align === "center" ? "justify-center" : "justify-start"
  return (
    <th class="px-3 py-2.5">
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        class={`flex items-center gap-1 w-full text-xs font-medium uppercase tracking-wide transition-colors ${alignCls} ${
          active ? "text-blue-600" : "text-gray-500 hover:text-gray-700"
        }`}
      >
        <span>{label}</span>
        {active ? (
          dir === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
        ) : (
          <ChevronsUpDown className="h-3 w-3 text-gray-300" />
        )}
      </button>
    </th>
  )
}

interface SummaryStatsProps {
  rows: AccrualRow[]
}

function SummaryStats({ rows }: SummaryStatsProps) {
  const totals = accrual_totals(rows)
  const has_plan = totals.with_plan > 0

  return (
    <div class="grid grid-cols-5 gap-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
      <Stat label="Promotions" value={String(totals.promotions)} />
      <Stat label="Total Planned" value={has_plan ? exactCurrencyFormatter(totals.planned) : "—"} sub={has_plan ? `${totals.with_plan} with plan` : "No plans set"} />
      <Stat
        label="Latest Estimate"
        value={has_plan || totals.with_own_estimate > 0 ? exactCurrencyFormatter(totals.latest_estimate) : "—"}
        sub={totals.with_own_estimate > 0 ? `${totals.with_own_estimate} re-forecast` : "Same as plan"}
      />
      <Stat label="Total Actual" value={exactCurrencyFormatter(totals.actual)} />
      <Stat
        label="Variance"
        value={has_plan ? exactCurrencyFormatter(Math.abs(totals.variance)) : "—"}
        sub={has_plan ? (totals.variance >= 0 ? "over plan" : "under plan") : undefined}
        valueClass={has_plan ? (totals.variance > 0 ? "text-red-600" : "text-emerald-600") : "text-gray-400"}
      />
    </div>
  )
}

interface StatProps {
  label: string
  value: string
  sub?: string
  valueClass?: string
}

function Stat({ label, value, sub, valueClass = "text-gray-900" }: StatProps) {
  return (
    <div>
      <p class="text-xs text-gray-500 mb-0.5">{label}</p>
      <p class={`text-lg font-semibold ${valueClass}`}>{value}</p>
      {sub && <p class="text-xs text-gray-400">{sub}</p>}
    </div>
  )
}

/** "1.9× last runs" (or "0.1× last runs") under a plan far from what the same event actually spent before. */
function LastRunBadge({ row }: { row: AccrualRow }) {
  const warning = last_run_warning(row.planned_amount, row.last_run)
  if (!warning) return null
  return (
    <div
      class="mt-0.5 text-[11px] font-medium text-amber-700"
      title={`Planned well ${warning.direction} the ${exactCurrencyFormatter(warning.reference.median_actual)} this promotion actually spent. ${last_run_title(warning.reference)}`}
      data-testid="last-run-badge"
    >
      {format_ratio(warning.ratio)} last {warning.reference.runs.length === 1 ? "run" : "runs"}
    </div>
  )
}

interface AccrualTableRowProps {
  row: AccrualRow
  onClick: () => void
}

function AccrualTableRow({ row, onClick }: AccrualTableRowProps) {
  const status = promotion_status(row)
  const variance = row.planned_amount !== null ? row.actual_amount - row.planned_amount : null
  const variancePct = row.planned_amount ? (variance! / row.planned_amount) * 100 : null
  const over = variance !== null && variance > 0

  return (
    <tr
      onClick={onClick}
      class="border-b border-gray-100 hover:bg-blue-50 cursor-pointer transition-colors"
    >
      <td class="px-3 py-3 text-sm">
        {row.remote_promo_id ? (
          <ClickToCopyRemotePromoId
            copyValue={row.remote_promo_id}
            stopPropagation
            className="font-mono text-xs font-medium text-blue-700 hover:underline"
          >
            {row.remote_promo_id}
          </ClickToCopyRemotePromoId>
        ) : (
          <span class="font-mono text-xs text-gray-400">—</span>
        )}
      </td>
      <td class="px-3 py-3 text-sm text-gray-700 max-w-[160px] truncate">
        {row.retailer ?? "—"}
      </td>
      <td class="px-3 py-3 text-sm text-gray-600 whitespace-nowrap">
        {row.customer ?? "—"}
      </td>
      <td class="px-3 py-3">
        {row.promotion_type ? (
          <span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700">
            {row.promotion_type}
          </span>
        ) : (
          <span class="text-gray-400 text-xs">—</span>
        )}
      </td>
      <td class="px-3 py-3 text-xs text-gray-500 whitespace-nowrap">
        {format_date_range(row.start_date, row.end_date)}
      </td>
      <td class="px-3 py-3 text-sm text-right">
        {row.planned_amount !== null ? (
          <span class="font-medium text-gray-800">{exactCurrencyFormatter(row.planned_amount)}</span>
        ) : (
          <span class="text-gray-300">—</span>
        )}
        <LastRunBadge row={row} />
      </td>
      <td class="px-3 py-3 text-sm text-right">
        {latest_estimate_of(row) !== null ? (
          // Muted when merely echoing the plan; solid once someone actually re-forecast it.
          <span class={row.latest_estimate_amount !== null ? "font-medium text-gray-800" : "text-gray-400"}>
            {exactCurrencyFormatter(latest_estimate_of(row)!)}
          </span>
        ) : (
          <span class="text-gray-300">—</span>
        )}
      </td>
      <td class="px-3 py-3 min-w-[130px]">
        <div class="text-sm font-medium text-gray-800 text-right">
          {exactCurrencyFormatter(row.actual_amount)}
        </div>
        {row.planned_amount !== null && row.planned_amount > 0 && (
          <ProgressBar actual={row.actual_amount} planned={row.planned_amount} />
        )}
      </td>
      <td class="px-3 py-3 text-sm text-right">
        {variance !== null ? (
          <span class={`font-medium ${over ? "text-red-600" : "text-emerald-600"}`}>
            {over ? "+" : ""}{exactCurrencyFormatter(variance)}
          </span>
        ) : (
          <span class="text-gray-300">—</span>
        )}
      </td>
      <td class="px-3 py-3 text-sm text-right">
        {variancePct !== null ? (
          <span class={`text-xs font-medium ${over ? "text-red-500" : "text-emerald-500"}`}>
            {over ? "+" : ""}{variancePct.toFixed(1)}%
          </span>
        ) : (
          <span class="text-gray-300">—</span>
        )}
      </td>
      <td class="px-3 py-3 text-sm text-center text-gray-500">
        {row.match_count}
      </td>
      <td class="px-3 py-3">
        <StatusBadge status={status} />
      </td>
    </tr>
  )
}

// ── Main component ────────────────────────────────────────────────────────────

export function AccrualSummaryView({
  selectedYear,
  onEditPromotion,
  filters,
  onFiltersChange,
  searchTerm,
  onSearchChange,
  className,
}: AccrualSummaryViewProps) {
  const { rows, loading, error } = use_accrual_rows(selectedYear)
  const [selectedMonths, setSelectedMonths] = useState<Set<number>>(new Set())
  const [monthBasis, setMonthBasis] = useState<MonthBasis>("promotion")
  // Default to surfacing the promotions matched to deductions first.
  const [sortKey, setSortKey] = useState<SortKey>("matches")
  const [sortDir, setSortDir] = useState<SortDir>("desc")

  const filterOptions = useMemo(() => promotion_filter_options(rows), [rows])

  const filtered = useMemo(() => {
    const selection = { ...filters, search: searchTerm }
    return rows.filter((r: AccrualRow) =>
      promotion_matches_filters(r, selection) && row_matches_months(r, selectedMonths, monthBasis, selectedYear),
    )
  }, [rows, filters, searchTerm, selectedMonths, monthBasis, selectedYear])

  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => compare_rows(a, b, sortKey, sortDir))
  }, [filtered, sortKey, sortDir])

  function handle_sort(key: SortKey) {
    setSortKey((prevKey) => {
      if (prevKey === key) {
        // Toggle direction when re-clicking the active column.
        setSortDir((d) => (d === "asc" ? "desc" : "asc"))
        return prevKey
      }
      // New column: amounts/counts default to descending, text to ascending.
      const numeric = key === "planned" || key === "latest_estimate" || key === "actual" || key === "variance" || key === "variance_pct" || key === "matches"
      setSortDir(numeric ? "desc" : "asc")
      return key
    })
  }

  function toggle_month(idx: number) {
    setSelectedMonths((prev) => {
      const next = new Set(prev)
      next.has(idx) ? next.delete(idx) : next.add(idx)
      return next
    })
  }

  function handle_row_click(row: AccrualRow) {
    // The page's promotion modal opens it and loads the rest of the promotion by id.
    const promo: Promotion = {
      id: row.id,
      remote_promo_id: row.remote_promo_id ?? undefined,
      name: row.name,
      retailer: row.retailer ?? undefined,
      customer: row.customer ?? undefined,
      promotion_type: row.promotion_type ?? undefined,
      start_date: row.start_date ?? undefined,
      end_date: row.end_date ?? undefined,
      products: [],
    }
    onEditPromotion(promo)
  }

  if (loading) {
    return (
      <div class="flex items-center justify-center py-20 text-gray-400">
        <Loader2 className="h-6 w-6 animate-spin mr-2" />
        Loading accrual data…
      </div>
    )
  }

  if (error) {
    return <div class="py-10 text-center text-red-500 text-sm">{error}</div>
  }

  return (
    <div class={className}>
      {/* Summary Stats */}
      <SummaryStats rows={filtered} />

      {/* Filters */}
      <div class="mt-4 space-y-3">
        {/* Month filter basis */}
        <div class="flex flex-wrap items-center gap-3">
          <span class="text-xs text-gray-500 whitespace-nowrap">Filter month by</span>
          <MonthBasisSelector value={monthBasis} onChange={setMonthBasis} />
        </div>

        {/* Month chips */}
        <div class="flex items-center gap-3">
          <span class="text-xs text-gray-500 whitespace-nowrap">Months ({selectedYear})</span>
          <MonthChips selectedMonths={selectedMonths} onToggle={toggle_month} />
        </div>
        <p class="text-xs text-gray-400">
          {selectedMonths.size === 0
            ? `Showing all months. Pick months to filter by ${MONTH_BASIS_OPTIONS.find((o) => o.value === monthBasis)!.hint.toLowerCase()}.`
            : `Filtering by ${MONTH_BASIS_OPTIONS.find((o) => o.value === monthBasis)!.hint.toLowerCase()}.`}
        </p>

        {/* The Calendar's filters, so a bucket filtered there reads the same here */}
        <CalendarFilters
          filterOptions={filterOptions}
          selectedCustomers={filters.customers}
          selectedRetailers={filters.retailers}
          selectedPromotionTypes={filters.promotion_types}
          selectedProducts={filters.products}
          searchTerm={searchTerm}
          onCustomerChange={(customers: string[]) => onFiltersChange({ ...filters, customers })}
          onRetailerChange={(retailers: string[]) => onFiltersChange({ ...filters, retailers })}
          onPromotionTypeChange={(promotion_types: string[]) => onFiltersChange({ ...filters, promotion_types })}
          onProductChange={(products: string[]) => onFiltersChange({ ...filters, products })}
          onSearchChange={onSearchChange}
        />
      </div>

      <div class="mt-4 flex justify-end">
        <PromotionDownloads promotion_ids={filtered.map((row: AccrualRow) => row.id)} />
      </div>

      {/* Table */}
      <div class="mt-2 rounded-lg border border-gray-200 overflow-x-auto">
        <table class="w-full text-left border-collapse">
          <thead>
            <tr class="bg-gray-50 border-b border-gray-200">
              <SortHeader label="Promo #" sortKey="promo" activeKey={sortKey} dir={sortDir} onSort={handle_sort} />
              <SortHeader label="Retailer" sortKey="retailer" activeKey={sortKey} dir={sortDir} onSort={handle_sort} />
              <SortHeader label="Customer" sortKey="customer" activeKey={sortKey} dir={sortDir} onSort={handle_sort} />
              <SortHeader label="Type" sortKey="type" activeKey={sortKey} dir={sortDir} onSort={handle_sort} />
              <SortHeader label="Dates" sortKey="dates" activeKey={sortKey} dir={sortDir} onSort={handle_sort} />
              <SortHeader label="Planned $" sortKey="planned" activeKey={sortKey} dir={sortDir} onSort={handle_sort} align="right" />
              <SortHeader label="Latest Est. $" sortKey="latest_estimate" activeKey={sortKey} dir={sortDir} onSort={handle_sort} align="right" />
              <SortHeader label="Actual $" sortKey="actual" activeKey={sortKey} dir={sortDir} onSort={handle_sort} align="right" />
              <SortHeader label="Variance $" sortKey="variance" activeKey={sortKey} dir={sortDir} onSort={handle_sort} align="right" />
              <SortHeader label="Var %" sortKey="variance_pct" activeKey={sortKey} dir={sortDir} onSort={handle_sort} align="right" />
              <SortHeader label="Matches" sortKey="matches" activeKey={sortKey} dir={sortDir} onSort={handle_sort} align="center" />
              <SortHeader label="Status" sortKey="status" activeKey={sortKey} dir={sortDir} onSort={handle_sort} />
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 ? (
              <tr>
                <td colspan={12} class="px-3 py-12 text-center text-sm text-gray-400">
                  No promotions found for the selected filters.
                </td>
              </tr>
            ) : (
              sorted.map((row) => (
                <AccrualTableRow key={row.id} row={row} onClick={() => handle_row_click(row)} />
              ))
            )}
          </tbody>
        </table>
      </div>

      {filtered.length > 0 && (
        <p class="mt-2 text-xs text-gray-400 text-right">
          {filtered.length} promotion{filtered.length !== 1 ? "s" : ""}
          {rows.length !== filtered.length ? ` (of ${rows.length} total)` : ""}
        </p>
      )}
    </div>
  )
}
